/**
 * WebView stub — renders HTML/URI in an iframe on PWA.
 * Exposes RN WebView imperative APIs used by classic banner media + payment.
 */
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { StyleSheet, View, type StyleProp, type ViewProps, type ViewStyle } from 'react-native';

export type ShouldStartLoadRequest = {
  url: string;
  navigationType?: string;
  mainDocumentURL?: string;
  isTopFrame?: boolean;
};

export type WebViewMessageEvent = {
  nativeEvent: { data: string };
};

export type WebViewNavigation = {
  url?: string;
  title?: string;
  loading?: boolean;
  canGoBack?: boolean;
  canGoForward?: boolean;
};

type Source = { uri?: string; html?: string; baseUrl?: string };

export type WebViewRef = {
  injectJavaScript: (script: string) => void;
  reload: () => void;
  goBack: () => void;
  goForward: () => void;
  stopLoading: () => void;
};

type Props = ViewProps & {
  source?: Source;
  style?: StyleProp<ViewStyle>;
  onLoadEnd?: (event?: { nativeEvent?: WebViewNavigation }) => void;
  onLoadStart?: () => void;
  onError?: (e: unknown) => void;
  onHttpError?: (e: unknown) => void;
  onMessage?: (event: WebViewMessageEvent) => void;
  onNavigationStateChange?: (nav: WebViewNavigation) => void;
  onShouldStartLoadWithRequest?: (request: ShouldStartLoadRequest) => boolean;
  originWhitelist?: string[];
  javaScriptEnabled?: boolean;
  domStorageEnabled?: boolean;
  startInLoadingState?: boolean;
  scalesPageToFit?: boolean;
  allowsInlineMediaPlayback?: boolean;
  mediaPlaybackRequiresUserAction?: boolean;
  mixedContentMode?: string;
  setSupportMultipleWindows?: boolean;
  scrollEnabled?: boolean;
  bounces?: boolean;
  allowFileAccess?: boolean;
  cacheEnabled?: boolean;
};

const BRIDGE_SCRIPT =
  `<script>` +
  `window.ReactNativeWebView={postMessage:function(d){` +
  `try{parent.postMessage({__rnwv:1,data:String(d)},'*')}catch(e){}` +
  `}};` +
  `</script>`;

function withReactNativeBridge(html: string): string {
  if (html.includes('ReactNativeWebView={postMessage')) {
    return html;
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, match => `${match}${BRIDGE_SCRIPT}`);
  }
  return `${BRIDGE_SCRIPT}${html}`;
}

function runInIframe(iframe: HTMLIFrameElement | null, script: string): void {
  if (!iframe) {
    return;
  }
  try {
    const win = iframe.contentWindow;
    if (!win) {
      return;
    }
    // srcDoc iframes are same-origin; remote uri may throw — swallow.
    win.eval(script);
  } catch {
    try {
      const win = iframe.contentWindow;
      if (!win) {
        return;
      }
      // eslint-disable-next-line no-new-func
      win.Function(script)();
    } catch {
      // ignore cross-origin / CSP
    }
  }
}

export const WebView = React.forwardRef<WebViewRef, Props>(function WebViewStub(
  {
    source,
    style,
    onLoadEnd,
    onLoadStart,
    onError,
    onMessage,
    onNavigationStateChange,
    pointerEvents,
    ...rest
  },
  ref,
) {
  const iframe_ref = useRef<HTMLIFrameElement | null>(null);
  const on_message_ref = useRef(onMessage);
  on_message_ref.current = onMessage;

  useImperativeHandle(
    ref,
    (): WebViewRef => ({
      injectJavaScript(script: string) {
        runInIframe(iframe_ref.current, script);
      },
      reload() {
        const el = iframe_ref.current;
        if (!el) {
          return;
        }
        try {
          el.contentWindow?.location.reload();
        } catch {
          const src = el.src;
          el.src = src;
        }
      },
      goBack() {
        try {
          iframe_ref.current?.contentWindow?.history.back();
        } catch {
          // ignore
        }
      },
      goForward() {
        try {
          iframe_ref.current?.contentWindow?.history.forward();
        } catch {
          // ignore
        }
      },
      stopLoading() {
        try {
          iframe_ref.current?.contentWindow?.stop?.();
        } catch {
          // ignore
        }
      },
    }),
    [],
  );

  useEffect(() => {
    const on_window_message = (event: MessageEvent) => {
      const data = event.data;
      if (
        data &&
        typeof data === 'object' &&
        (data as { __rnwv?: number }).__rnwv === 1 &&
        typeof (data as { data?: unknown }).data === 'string'
      ) {
        on_message_ref.current?.({ nativeEvent: { data: (data as { data: string }).data } });
      }
    };
    window.addEventListener('message', on_window_message);
    return () => window.removeEventListener('message', on_window_message);
  }, []);

  const handle_load = useCallback(() => {
    const url = source?.uri || (source?.html ? 'about:srcdoc' : '');
    onLoadEnd?.({ nativeEvent: { url, loading: false } });
    onNavigationStateChange?.({ url, loading: false });
  }, [onLoadEnd, onNavigationStateChange, source?.html, source?.uri]);

  useEffect(() => {
    if (source?.uri || source?.html) {
      onLoadStart?.();
    }
  }, [source?.uri, source?.html, onLoadStart]);

  const flat_style = StyleSheet.flatten(style) as ViewStyle | undefined;
  const style_bg = flat_style?.backgroundColor;
  const frame_bg = typeof style_bg === 'string' ? style_bg : 'transparent';

  const frame_style: React.CSSProperties = {
    border: 'none',
    width: '100%',
    height: '100%',
    display: 'block',
    backgroundColor: frame_bg,
  };

  const host_style = pointerEvents != null ? [{ flex: 1 }, style, { pointerEvents }] : [{ flex: 1 }, style];

  if (source?.html) {
    return (
      <View style={host_style} {...rest}>
        <iframe
          ref={iframe_ref}
          title="webview"
          srcDoc={withReactNativeBridge(source.html)}
          style={frame_style}
          allow="autoplay; encrypted-media; fullscreen"
          onLoad={handle_load}
          onError={() => onError?.({ nativeEvent: { description: 'iframe error' } })}
        />
      </View>
    );
  }

  if (source?.uri) {
    return (
      <View style={host_style} {...rest}>
        <iframe
          ref={iframe_ref}
          title="webview"
          src={source.uri}
          style={frame_style}
          allow="autoplay; encrypted-media; fullscreen"
          onLoad={handle_load}
          onError={() => onError?.({ nativeEvent: { description: 'iframe error' } })}
        />
      </View>
    );
  }

  return <View style={host_style} {...rest} />;
});

export default WebView;
